import { mediaQueryLarge, requestIdleCallback, startViewTransition, debounce } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';
import PaginatedList from '@theme/paginated-list';

const COLLECTION_GRID_SEARCH_EVENT = 'collection-grid-search';

const COLLECTION_SEARCH_STOP_WORDS = new Set([
  'beer',
  'beers',
  'drink',
  'drinks',
  'product',
  'products',
]);

const COLLECTION_STRUCTURED_SEARCH_PHRASES = new Map([
  [
    'pale ale',
    {
      fields: ['beerVarietyText'],
      terms: ['pale ale', 'american pale ale', 'new england pale ale', 'hazy pale', 'extra pale ale', 'apa', 'xpa'],
      excludes: ['india pale ale', 'ipa'],
    },
  ],
  [
    'india pale ale',
    {
      fields: ['beerVarietyText'],
      terms: ['ipa', 'american ipa', 'session ipa', 'west coast ipa', 'new england ipa', 'hazy ipa', 'black ipa', 'dipa', 'tipa'],
    },
  ],
  [
    'new england ipa',
    {
      fields: ['beerVarietyText'],
      terms: ['new england ipa', 'new england hazy ipa', 'hazy ipa'],
    },
  ],
  [
    'hazy ipa',
    {
      fields: ['beerVarietyText'],
      terms: ['hazy ipa', 'new england ipa', 'new england hazy ipa'],
    },
  ],
  [
    'session ipa',
    {
      fields: ['beerVarietyText'],
      terms: ['session ipa'],
    },
  ],
  [
    'double ipa',
    {
      fields: ['beerVarietyText'],
      terms: ['double ipa', 'dipa', 'imperial ipa'],
    },
  ],
  [
    'triple ipa',
    {
      fields: ['beerVarietyText'],
      terms: ['triple ipa', 'tipa'],
    },
  ],
  [
    'black ipa',
    {
      fields: ['beerVarietyText'],
      terms: ['black ipa'],
    },
  ],
  [
    'wheat beer',
    {
      fields: ['beerVarietyText'],
      terms: ['wheat beer', 'weissbier', 'weizen', 'hefeweizen', 'witbier', 'blanche'],
    },
  ],
  [
    'dark beer',
    {
      fields: ['beerVarietyText', 'beerStyleText'],
      terms: ['dark', 'dunkel', 'stout', 'porter', 'schwarzbier', 'brown ale', 'strong dark'],
    },
  ],
  [
    'fruit beer',
    {
      fields: ['beerVarietyText'],
      terms: ['fruit beer', 'fruited', 'kriek', 'lambic'],
    },
  ],
  [
    'smoked beer',
    {
      fields: ['beerVarietyText'],
      terms: ['smoked beer', 'rauchbier', 'rauch'],
    },
  ],
  [
    'strong beer',
    {
      fields: ['beerVarietyText'],
      terms: ['imperial', 'dubbel', 'tripel', 'triple', 'quadrupel', 'quad', 'bock', 'doppelbock', 'barleywine'],
    },
  ],
]);

const COLLECTION_STRUCTURED_SEARCH_TOKENS = new Map([
  [
    'ipa',
    {
      fields: ['beerVarietyText'],
      terms: ['ipa', 'american ipa', 'session ipa', 'west coast ipa', 'new england ipa', 'hazy ipa', 'black ipa', 'dipa', 'tipa'],
    },
  ],
  [
    'lager',
    {
      fields: ['beerVarietyText'],
      terms: ['lager', 'helles', 'pilsner', 'pils', 'kellerbier', 'zwickel', 'export'],
    },
  ],
  [
    'larger',
    {
      fields: ['beerVarietyText'],
      terms: ['lager', 'helles', 'pilsner', 'pils', 'kellerbier', 'zwickel', 'export'],
    },
  ],
  [
    'pilsner',
    {
      fields: ['beerVarietyText'],
      terms: ['pilsner', 'pils'],
    },
  ],
  [
    'pils',
    {
      fields: ['beerVarietyText'],
      terms: ['pilsner', 'pils'],
    },
  ],
  [
    'stout',
    {
      fields: ['beerVarietyText'],
      terms: ['stout', 'imperial stout', 'milk stout', 'oatmeal stout'],
    },
  ],
  [
    'porter',
    {
      fields: ['beerVarietyText'],
      terms: ['porter'],
    },
  ],
  [
    'sour',
    {
      fields: ['beerVarietyText'],
      terms: ['sour', 'gose', 'lambic', 'gueuze', 'geuze', 'kriek', 'berliner weisse', 'oud bruin', 'flanders red'],
    },
  ],
  [
    'wheat',
    {
      fields: ['beerVarietyText'],
      terms: ['wheat beer', 'weissbier', 'weizen', 'hefeweizen', 'witbier', 'blanche'],
    },
  ],
  [
    'bitter',
    {
      fields: ['beerVarietyText'],
      terms: ['bitter', 'best bitter', 'english bitter', 'esb'],
    },
  ],
  [
    'bock',
    {
      fields: ['beerVarietyText'],
      terms: ['bock', 'doppelbock', 'eisbock', 'maibock', 'weizenbock'],
    },
  ],
  [
    'radler',
    {
      fields: ['beerVarietyText'],
      terms: ['radler', 'shandy'],
    },
  ],
  [
    'shandy',
    {
      fields: ['beerVarietyText'],
      terms: ['shandy', 'radler'],
    },
  ],
]);

const COLLECTION_SEARCH_PHRASE_ALIASES = new Map([
  [
    'alcohol free',
    [
      'alcohol free',
      'alcohol-free',
      'non alcoholic',
      'non-alcoholic',
      'alkoholfrei',
      'zero alcohol',
      '0.0',
      '0.5',
    ],
  ],
  [
    'low alcohol',
    [
      'low alcohol',
      'low-alcohol',
      'alcohol free',
      'alcohol-free',
      'non alcoholic',
      'non-alcoholic',
      'alkoholfrei',
      '0.0',
      '0.5',
    ],
  ],
  ['gluten free', ['gluten free', 'gluten-free']],
  ['wheat beer', ['wheat beer', 'wheat', 'weissbier', 'weizen', 'hefeweizen', 'witbier', 'blanche']],
  ['dark beer', ['dark beer', 'dark', 'dunkel', 'stout', 'porter', 'schwarzbier', 'brown ale']],
  ['fruit beer', ['fruit beer', 'fruit', 'fruited', 'fruity', 'kriek', 'lambic']],
  ['smoked beer', ['smoked beer', 'smoked', 'rauchbier', 'rauch']],
  ['strong beer', ['strong beer', 'strong', 'imperial', 'dubbel', 'tripel', 'triple', 'quadrupel', 'quad', 'bock']],
  ['pale ale', ['pale ale', 'apa', 'american pale ale', 'new england pale ale', 'extra pale ale', 'hazy pale']],
  ['india pale ale', ['india pale ale', 'ipa']],
  ['new england ipa', ['new england ipa', 'neipa', 'hazy ipa']],
  ['double ipa', ['double ipa', 'dipa', 'imperial ipa']],
  ['triple ipa', ['triple ipa', 'tipa']],
  ['red wine', ['red wine', 'red']],
  ['white wine', ['white wine', 'white']],
  ['rose wine', ['rose wine', 'rosé wine', 'rose', 'rosé']],
  ['sparkling wine', ['sparkling wine', 'sparkling', 'prosecco', 'champagne', 'cava']],
]);

const COLLECTION_SEARCH_TOKEN_ALIASES = new Map([
  ['lager', ['lager', 'helles', 'pilsner', 'pils', 'kellerbier', 'zwickel', 'export']],
  ['larger', ['lager', 'helles', 'pilsner', 'pils']],
  ['pilsner', ['pilsner', 'pils']],
  ['pils', ['pils', 'pilsner']],
  ['wheat', ['wheat', 'weissbier', 'weizen', 'hefeweizen', 'witbier', 'blanche']],
  ['stout', ['stout', 'porter', 'imperial stout', 'milk stout', 'oatmeal stout']],
  ['porter', ['porter', 'stout']],
  ['sour', ['sour', 'gose', 'lambic', 'gueuze', 'geuze', 'kriek', 'berliner weisse', 'oud bruin', 'flanders red']],
  ['fruity', ['fruity', 'fruit', 'fruited', 'kriek', 'cherry', 'raspberry', 'peach', 'apple', 'cassis']],
  ['fruit', ['fruit', 'fruited', 'fruity', 'kriek', 'cherry', 'raspberry', 'peach', 'apple', 'cassis']],
  ['dark', ['dark', 'dunkel', 'stout', 'porter', 'schwarzbier', 'brown ale', 'strong dark']],
  ['strong', ['strong', 'imperial', 'dubbel', 'tripel', 'triple', 'quadrupel', 'quad', 'bock', 'doppelbock', 'barleywine']],
  ['smoked', ['smoked', 'rauchbier', 'rauch']],
  ['ipa', ['ipa', 'india pale ale', 'neipa', 'dipa', 'tipa']],
  ['hazy', ['hazy', 'neipa', 'new england ipa']],
  ['pale', ['pale', 'pale ale', 'apa']],
  ['blonde', ['blonde', 'blond', 'golden ale']],
  ['blond', ['blond', 'blonde', 'golden ale']],
  ['amber', ['amber', 'red ale', 'rye ale']],
  ['red', ['red', 'red ale', 'amber']],
  ['bitter', ['bitter', 'best bitter', 'english bitter', 'esb']],
  ['bock', ['bock', 'doppelbock', 'eisbock', 'maibock', 'weizenbock']],
  ['radler', ['radler', 'shandy']],
  ['shandy', ['shandy', 'radler']],
  ['trappist', ['trappist', 'trappistenbier']],
  ['abbey', ['abbey', 'abbaye', 'abdij']],
  ['vegan', ['vegan', 'plant based', 'plant-based']],
  ['organic', ['organic', 'bio']],
  ['can', ['can', 'cans', 'canned']],
  ['cans', ['can', 'cans', 'canned']],
  ['bottle', ['bottle', 'bottles', 'bottled']],
  ['bottles', ['bottle', 'bottles', 'bottled']],
  ['german', ['german', 'germany', 'deutschland', 'deutsch']],
  ['germany', ['germany', 'german', 'deutschland', 'deutsch']],
  ['belgian', ['belgian', 'belgium', 'belgique']],
  ['belgium', ['belgium', 'belgian', 'belgique']],
  ['dutch', ['dutch', 'netherlands', 'holland']],
  ['british', ['british', 'britain', 'uk', 'england', 'scotland', 'wales', 'northern ireland']],
  ['uk', ['uk', 'british', 'britain', 'england', 'scotland', 'wales', 'northern ireland']],
  ['english', ['english', 'england', 'british', 'britain', 'uk']],
  ['scottish', ['scottish', 'scotland', 'british', 'britain', 'uk']],
  ['welsh', ['welsh', 'wales', 'british', 'britain', 'uk']],
  ['irish', ['irish', 'ireland']],
  ['american', ['american', 'america', 'usa', 'united states', 'us']],
  ['usa', ['usa', 'american', 'america', 'united states', 'us']],
  ['french', ['french', 'france']],
  ['italian', ['italian', 'italy']],
  ['spanish', ['spanish', 'spain']],
  ['portuguese', ['portuguese', 'portugal']],
  ['japanese', ['japanese', 'japan']],
  ['mexican', ['mexican', 'mexico']],
  ['polish', ['polish', 'poland']],
  ['norwegian', ['norwegian', 'norway']],
  ['austrian', ['austrian', 'austria']],
  ['czech', ['czech', 'czechia', 'czech republic']],
  ['whisky', ['whisky', 'whiskey']],
  ['whiskey', ['whiskey', 'whisky']],
  ['scotch', ['scotch', 'scottish whisky', 'single malt']],
  ['fizz', ['fizz', 'sparkling', 'prosecco', 'champagne', 'cava']],
]);

/**
 * @param {string} sectionId
 * @returns {string}
 */
function getCollectionSearchStorageKey(sectionId) {
  return `collection-grid-search:${window.location.pathname}:${sectionId}`;
}

/**
 * Search field that filters the actual product grid for the current collection.
 */
class CollectionGridSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('.collection-grid-search__input');
    this.clearButton = this.querySelector('.collection-grid-search__clear');
    this.toggleButton = this.querySelector('.collection-grid-search__toggle');

    if (
      !(this.input instanceof HTMLInputElement) ||
      !(this.clearButton instanceof HTMLButtonElement) ||
      !(this.toggleButton instanceof HTMLButtonElement)
    ) {
      return;
    }

    const savedQuery = sessionStorage.getItem(getCollectionSearchStorageKey(this.sectionId)) ?? '';
    this.input.value = savedQuery;
    this.clearButton.hidden = savedQuery.length === 0;
    this.#setOpen(savedQuery.length > 0, false);

    this.toggleButton.addEventListener('click', this.#handleToggle);
    this.input.addEventListener('input', this.#handleInput);
    this.input.addEventListener('keydown', this.#handleKeyDown);
    this.clearButton.addEventListener('click', this.#clearSearch);
    document.addEventListener('pointerdown', this.#handleOutsideClick);
    document.addEventListener(COLLECTION_GRID_SEARCH_EVENT, this.#syncSearchFields);
  }

  disconnectedCallback() {
    this.toggleButton?.removeEventListener('click', this.#handleToggle);
    this.input?.removeEventListener('input', this.#handleInput);
    this.input?.removeEventListener('keydown', this.#handleKeyDown);
    this.clearButton?.removeEventListener('click', this.#clearSearch);
    document.removeEventListener('pointerdown', this.#handleOutsideClick);
    document.removeEventListener(COLLECTION_GRID_SEARCH_EVENT, this.#syncSearchFields);
    this.#dispatchSearch.cancel();
  }

  get sectionId() {
    return this.getAttribute('section-id') ?? '';
  }

  #handleToggle = () => {
    if (!(this.input instanceof HTMLInputElement)) return;

    if (!this.hasAttribute('open')) {
      this.#setOpen(true, true);
      return;
    }

    if (this.input.value.trim()) {
      this.input.focus();
      return;
    }

    this.#setOpen(false, false);
  };

  #handleInput = () => {
    if (!(this.input instanceof HTMLInputElement) || !(this.clearButton instanceof HTMLButtonElement)) return;

    const hasQuery = this.input.value.trim().length > 0;
    this.clearButton.hidden = !hasQuery;

    if (!this.hasAttribute('open')) {
      this.#setOpen(true, false);
    }

    this.#dispatchSearch();
  };

  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    if (!(this.input instanceof HTMLInputElement)) return;

    if (this.input.value.trim()) {
      this.input.select();
      return;
    }

    this.#setOpen(false, false);
    this.toggleButton?.focus();
  };

  #handleOutsideClick = (event) => {
    if (!(event.target instanceof Node)) return;
    if (this.contains(event.target)) return;
    if (!(this.input instanceof HTMLInputElement)) return;
    if (this.input.value.trim()) return;

    this.#setOpen(false, false);
  };

  #dispatchSearch = debounce(() => {
    if (!(this.input instanceof HTMLInputElement)) return;
    this.#emitSearch(this.input.value.trim());
  }, 220);

  #clearSearch = () => {
    if (!(this.input instanceof HTMLInputElement) || !(this.clearButton instanceof HTMLButtonElement)) return;

    this.#dispatchSearch.cancel();
    this.input.value = '';
    this.clearButton.hidden = true;
    this.#emitSearch('');
    this.#setOpen(true, true);
  };

  /**
   * @param {boolean} shouldOpen
   * @param {boolean} shouldFocus
   */
  #setOpen(shouldOpen, shouldFocus) {
    this.toggleAttribute('open', shouldOpen);
    this.toggleButton?.setAttribute('aria-expanded', String(shouldOpen));

    if (shouldFocus) {
      requestAnimationFrame(() => this.input?.focus());
    }
  }

  /**
   * @param {string} query
   */
  #emitSearch(query) {
    const storageKey = getCollectionSearchStorageKey(this.sectionId);

    if (query) {
      sessionStorage.setItem(storageKey, query);
    } else {
      sessionStorage.removeItem(storageKey);
    }

    document.dispatchEvent(
      new CustomEvent(COLLECTION_GRID_SEARCH_EVENT, {
        detail: {
          sectionId: this.sectionId,
          query,
          source: this,
        },
      })
    );
  }

  /**
   * Keeps the desktop and mobile search fields synchronized.
   * @param {CustomEvent} event
   */
  #syncSearchFields = (event) => {
    if (event.detail?.sectionId !== this.sectionId || event.detail?.source === this) return;

    if (
      !(this.input instanceof HTMLInputElement) ||
      !(this.clearButton instanceof HTMLButtonElement)
    ) {
      return;
    }

    const query = String(event.detail?.query ?? '');
    this.input.value = query;
    this.clearButton.hidden = query.length === 0;

    if (query) {
      this.#setOpen(true, false);
    }
  };
}

if (!customElements.get('collection-grid-search')) {
  customElements.define('collection-grid-search', CollectionGridSearch);
}

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  #searchIndex = null;
  #searchIndexKey = '';
  #searchIndexPromise = null;
  #searchAbortController = null;
  #searchRequestId = 0;
  #activeSearchQuery = '';
  #originalCountLabels = [];
  #sourceLastPage = 1;
  #sourceTotalItems = 0;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    document.addEventListener(COLLECTION_GRID_SEARCH_EVENT, this.#handleCollectionSearch);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdateForSearch);

    this.#captureOriginalCountLabels();
    this.#captureSourceCollectionState();
    this.setAttribute('initialized', '');

    const savedQuery = sessionStorage.getItem(getCollectionSearchStorageKey(this.sectionId)) ?? '';
    if (savedQuery) {
      requestIdleCallback(() => {
        if (this.isConnected) this.#applyCollectionSearch(savedQuery);
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    document.removeEventListener(COLLECTION_GRID_SEARCH_EVENT, this.#handleCollectionSearch);
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdateForSearch);

    this.#searchRequestId += 1;
    this.#searchAbortController?.abort();
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };

  /**
   * @param {CustomEvent} event
   */
  #handleCollectionSearch = (event) => {
    if (event.detail?.sectionId !== this.sectionId) return;

    const query = String(event.detail?.query ?? '').trim();

    if (!query) {
      this.#clearCollectionSearch();
      return;
    }

    this.#applyCollectionSearch(query);
  };

  #handleFilterUpdateForSearch = () => {
    this.#searchRequestId += 1;
    this.#searchAbortController?.abort();
    this.#searchAbortController = null;
    this.#searchIndex = null;
    this.#searchIndexKey = '';
    this.#searchIndexPromise = null;
    this.#sourceLastPage = 1;
    this.#sourceTotalItems = 0;
  };

  /**
   * @param {string} query
   */
  async #applyCollectionSearch(query) {
    const { grid } = this.refs;
    if (!grid) return;

    this.#activeSearchQuery = query;
    sessionStorage.setItem(getCollectionSearchStorageKey(this.sectionId), query);

    const requestId = ++this.#searchRequestId;
    this.infinityScrollObserver?.disconnect();
    this.setAttribute('collection-searching', '');
    this.#setCountLabel('Searching…');

    try {
      const index = await this.#getSearchIndex();

      if (requestId !== this.#searchRequestId || !this.isConnected) return;

      const searchGroups = this.#createSearchGroups(query);
      const matches = index.filter((record) =>
        searchGroups.every((group) => this.#recordMatchesSearchGroup(record, group))
      );

      await startViewTransition(() => {
        if (!this.refs.grid) return;

        if (matches.length > 0) {
          const cards = matches.map((record) => document.importNode(record.element, true));
          this.refs.grid.replaceChildren(...cards);
        } else {
          this.refs.grid.replaceChildren(this.#createEmptySearchMessage(query));
        }

        this.refs.grid.dataset.collectionSearchActive = 'true';
        this.#setCountLabel(matches.length);
      }, ['product-grid']);
    } catch (error) {
      if (error?.name === 'AbortError') return;

      console.error('Collection grid search failed:', error);

      if (requestId === this.#searchRequestId) {
        this.#setCountLabel('Search unavailable');
      }
    } finally {
      if (requestId === this.#searchRequestId) {
        this.removeAttribute('collection-searching');
      }
    }
  }

  async #clearCollectionSearch() {
    const { grid } = this.refs;
    const wasActive = Boolean(this.#activeSearchQuery || grid?.dataset.collectionSearchActive === 'true');

    this.#activeSearchQuery = '';
    this.#searchRequestId += 1;
    this.#searchAbortController?.abort();
    this.#searchAbortController = null;
    sessionStorage.removeItem(getCollectionSearchStorageKey(this.sectionId));

    if (!wasActive) {
      this.#restoreCountLabels();
      return;
    }

    await sectionRenderer.renderSection(this.sectionId);
  }

  #captureSourceCollectionState() {
    const { grid } = this.refs;
    if (!grid) return;

    this.#sourceLastPage = Math.max(1, Number(grid.dataset.lastPage) || 1);
    this.#sourceTotalItems = Math.max(
      0,
      Number(this.getAttribute('total-items')) ||
        Number(grid.dataset.totalItems) ||
        0
    );
  }

  async #getSearchIndex() {
    const { grid } = this.refs;
    if (!grid) return [];

    if (this.#sourceLastPage < 1) {
      this.#captureSourceCollectionState();
    }

    const lastPage = Math.max(1, this.#sourceLastPage);
    const baseUrl = new URL(window.location.href);
    baseUrl.searchParams.delete('page');
    baseUrl.searchParams.delete('_hbb_collection_search');
    baseUrl.hash = '';

    const cacheKey = `${baseUrl.pathname}?${baseUrl.searchParams.toString()}|pages:${lastPage}|items:${this.#sourceTotalItems}`;

    if (this.#searchIndex && this.#searchIndexKey === cacheKey) {
      return this.#searchIndex;
    }

    if (this.#searchIndexPromise && this.#searchIndexKey === cacheKey) {
      return this.#searchIndexPromise;
    }

    this.#searchAbortController?.abort();
    this.#searchAbortController = new AbortController();
    this.#searchIndexKey = cacheKey;

    this.#searchIndexPromise = this.#buildSearchIndex(
      baseUrl,
      lastPage,
      this.#sourceTotalItems,
      this.#searchAbortController.signal
    );

    try {
      this.#searchIndex = await this.#searchIndexPromise;
      return this.#searchIndex;
    } finally {
      this.#searchIndexPromise = null;
    }
  }

  /**
   * Loads every collection page in order. Sequential requests are deliberate:
   * they avoid occasional section-renderer cache collisions seen when several
   * collection pages are requested at the same time.
   *
   * @param {URL} baseUrl
   * @param {number} lastPage
   * @param {number} expectedTotal
   * @param {AbortSignal} signal
   */
  async #buildSearchIndex(baseUrl, lastPage, expectedTotal, signal) {
    let records = await this.#loadAllSearchPages(baseUrl, lastPage, signal, false);

    // Shopify should return the exact filtered collection total. If a transient
    // response returned too few cards, repeat once with cache-busting URLs.
    if (expectedTotal > 0 && records.length < expectedTotal && !signal.aborted) {
      records = await this.#loadAllSearchPages(baseUrl, lastPage, signal, true);
    }

    if (expectedTotal > 0 && records.length < expectedTotal) {
      console.warn(
        `Collection search indexed ${records.length} of ${expectedTotal} products.`
      );
    }

    return records;
  }

  /**
   * @param {URL} baseUrl
   * @param {number} lastPage
   * @param {AbortSignal} signal
   * @param {boolean} bypassCache
   */
  async #loadAllSearchPages(baseUrl, lastPage, signal, bypassCache) {
    const records = [];
    const seenProductIds = new Set();

    for (let pageNumber = 1; pageNumber <= lastPage; pageNumber += 1) {
      if (signal.aborted) throw new DOMException('Search cancelled', 'AbortError');

      const pageUrl = new URL(baseUrl);
      pageUrl.searchParams.set('page', String(pageNumber));

      if (bypassCache) {
        pageUrl.searchParams.set(
          '_hbb_collection_search',
          `${Date.now()}-${pageNumber}`
        );
      }

      const pageRecords = await this.#fetchSearchPage(pageUrl, signal);

      for (const record of pageRecords) {
        if (record.productId && seenProductIds.has(record.productId)) continue;

        if (record.productId) {
          seenProductIds.add(record.productId);
        }

        records.push(record);
      }
    }

    return records;
  }

  /**
   * @param {URL} pageUrl
   * @param {AbortSignal} signal
   */
  async #fetchSearchPage(pageUrl, signal) {
    let lastError;

    for (let attempt = 1; attempt <= 2; attempt += 1) {
      try {
        const requestUrl = new URL(pageUrl);

        if (attempt > 1) {
          requestUrl.searchParams.set(
            '_hbb_collection_search',
            `${Date.now()}-retry`
          );
        }

        const pageHTML = await sectionRenderer.getSectionHTML(
          this.sectionId,
          true,
          requestUrl
        );

        if (signal.aborted) {
          throw new DOMException('Search cancelled', 'AbortError');
        }

        const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
        const pageGrid = parsedPage.querySelector('[ref="grid"]');

        if (!pageGrid) {
          throw new Error(`Collection grid missing for ${requestUrl.toString()}`);
        }

        const cards = Array.from(
          pageGrid.querySelectorAll(':scope > [ref="cards[]"]')
        );

        return cards.map((card) => {
          const searchText = this.#normalize(
            card.getAttribute('data-search-text') || card.textContent || ''
          );

          return {
            productId:
              card.getAttribute('data-product-id') ||
              card.id ||
              '',
            element: card,
            searchText,
            searchTokens: [...new Set(searchText.split(' ').filter(Boolean))],
            beerVarietyText: this.#normalize(card.getAttribute('data-search-beer-variety') || ''),
            beerStyleText: this.#normalize(card.getAttribute('data-search-beer-style') || ''),
            categoryFallbackText: this.#normalize(card.getAttribute('data-search-category-fallback') || ''),
          };
        });
      } catch (error) {
        if (error?.name === 'AbortError') throw error;
        lastError = error;
      }
    }

    throw lastError ?? new Error('Unable to load collection search page');
  }

  /**
   * Converts a customer query into AND groups.
   * Recognised beer categories use structured Shopify metafields first.
   *
   * @param {string} query
   * @returns {Array<object>}
   */
  #createSearchGroups(query) {
    let remainingQuery = ` ${this.#normalize(query)} `;
    const groups = [];

    const structuredPhrases = [...COLLECTION_STRUCTURED_SEARCH_PHRASES.entries()].sort(
      ([firstPhrase], [secondPhrase]) => secondPhrase.length - firstPhrase.length
    );

    for (const [phrase, rule] of structuredPhrases) {
      const normalizedPhrase = this.#normalize(phrase);
      const phrasePattern = ` ${normalizedPhrase} `;

      if (!remainingQuery.includes(phrasePattern)) continue;

      groups.push({
        type: 'structured',
        fields: rule.fields,
        terms: this.#normalizeAlternatives(rule.terms),
        excludes: this.#normalizeAlternatives(rule.excludes ?? []),
      });

      remainingQuery = remainingQuery.replace(phrasePattern, ' ');
    }

    const phraseEntries = [...COLLECTION_SEARCH_PHRASE_ALIASES.entries()].sort(
      ([firstPhrase], [secondPhrase]) => secondPhrase.length - firstPhrase.length
    );

    for (const [phrase, aliases] of phraseEntries) {
      const normalizedPhrase = this.#normalize(phrase);
      const phrasePattern = ` ${normalizedPhrase} `;

      if (!remainingQuery.includes(phrasePattern)) continue;

      groups.push({
        type: 'text',
        alternatives: this.#normalizeAlternatives(aliases),
      });

      remainingQuery = remainingQuery.replace(phrasePattern, ' ');
    }

    const remainingTerms = remainingQuery.trim().split(' ').filter(Boolean);

    for (const term of remainingTerms) {
      if (COLLECTION_SEARCH_STOP_WORDS.has(term)) continue;

      const structuredRule = COLLECTION_STRUCTURED_SEARCH_TOKENS.get(term);

      if (structuredRule) {
        groups.push({
          type: 'structured',
          fields: structuredRule.fields,
          terms: this.#normalizeAlternatives(structuredRule.terms),
          excludes: this.#normalizeAlternatives(structuredRule.excludes ?? []),
        });
        continue;
      }

      const aliases = COLLECTION_SEARCH_TOKEN_ALIASES.get(term) ?? [term];
      const alternatives = [...aliases];

      if (term.length > 3 && term.endsWith('s')) {
        alternatives.push(term.slice(0, -1));
      } else if (term.length > 3) {
        alternatives.push(`${term}s`);
      }

      groups.push({
        type: 'text',
        alternatives: this.#normalizeAlternatives(alternatives),
      });
    }

    return groups.length > 0
      ? groups
      : [{ type: 'text', alternatives: [''] }];
  }

  /**
   * @param {string[]} alternatives
   * @returns {string[]}
   */
  #normalizeAlternatives(alternatives) {
    return [...new Set(alternatives.map((value) => this.#normalize(value)).filter(Boolean))];
  }

  /**
   * @param {object} record
   * @param {object} group
   * @returns {boolean}
   */
  #recordMatchesSearchGroup(record, group) {
    if (group.type === 'structured') {
      return this.#recordMatchesStructuredGroup(record, group);
    }

    return group.alternatives.some((alternative) =>
      this.#recordMatchesAlternative(record, alternative)
    );
  }

  /**
   * Uses the Beer Variety metafield as the source of truth. Product type and
   * tags are only used when the relevant metafield is blank.
   *
   * @param {object} record
   * @param {{fields: string[], terms: string[], excludes: string[]}} group
   * @returns {boolean}
   */
  #recordMatchesStructuredGroup(record, group) {
    const structuredValues = group.fields
      .map((field) => String(record[field] ?? '').trim())
      .filter(Boolean);

    const searchableValue =
      structuredValues.length > 0
        ? structuredValues.join(' ')
        : String(record.categoryFallbackText ?? '');

    if (!searchableValue) return false;

    const excluded = group.excludes.some((term) =>
      this.#structuredTextContains(searchableValue, term)
    );

    if (excluded) return false;

    return group.terms.some((term) =>
      this.#structuredTextContains(searchableValue, term)
    );
  }

  /**
   * @param {string} text
   * @param {string} term
   * @returns {boolean}
   */
  #structuredTextContains(text, term) {
    if (!term) return true;

    const paddedText = ` ${text} `;
    const paddedTerm = ` ${term} `;

    if (paddedText.includes(paddedTerm)) return true;

    // Multi-word values can be part of a longer Shopify label, for example
    // "American pale ale (APA)" or "New England Pale Ale / Hazy Pale".
    if (term.includes(' ') && text.includes(term)) return true;

    return false;
  }

  /**
   * @param {{searchText: string, searchTokens: string[]}} record
   * @param {string} alternative
   * @returns {boolean}
   */
  #recordMatchesAlternative(record, alternative) {
    if (!alternative) return true;
    if (record.searchText.includes(alternative)) return true;

    const alternativeWords = alternative.split(' ').filter(Boolean);

    if (alternativeWords.length > 1) {
      return alternativeWords.every((word) => this.#recordMatchesWord(record, word));
    }

    return this.#recordMatchesWord(record, alternative);
  }

  /**
   * Allows minor spelling mistakes for names and ordinary free-text searches.
   * Structured beer categories are deliberately not fuzzy-matched.
   *
   * @param {{searchText: string, searchTokens: string[]}} record
   * @param {string} word
   * @returns {boolean}
   */
  #recordMatchesWord(record, word) {
    if (record.searchText.includes(word)) return true;
    if (word.length < 5) return false;

    const maximumDistance = word.length >= 9 ? 2 : 1;

    return record.searchTokens.some((token) => {
      if (Math.abs(token.length - word.length) > maximumDistance) return false;
      return this.#editDistanceWithin(token, word, maximumDistance);
    });
  }

  /**
   * Fast bounded Levenshtein comparison.
   *
   * @param {string} first
   * @param {string} second
   * @param {number} maximumDistance
   * @returns {boolean}
   */
  #editDistanceWithin(first, second, maximumDistance) {
    if (first === second) return true;
    if (Math.abs(first.length - second.length) > maximumDistance) return false;

    let previousRow = Array.from({ length: second.length + 1 }, (_, index) => index);

    for (let firstIndex = 1; firstIndex <= first.length; firstIndex += 1) {
      const currentRow = [firstIndex];
      let rowMinimum = currentRow[0];

      for (let secondIndex = 1; secondIndex <= second.length; secondIndex += 1) {
        const substitutionCost = first[firstIndex - 1] === second[secondIndex - 1] ? 0 : 1;

        const value = Math.min(
          previousRow[secondIndex] + 1,
          currentRow[secondIndex - 1] + 1,
          previousRow[secondIndex - 1] + substitutionCost
        );

        currentRow.push(value);
        rowMinimum = Math.min(rowMinimum, value);
      }

      if (rowMinimum > maximumDistance) return false;
      previousRow = currentRow;
    }

    return previousRow[second.length] <= maximumDistance;
  }

  /**
   * @param {string} value
   */
  #normalize(value) {
    return String(value)
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLocaleLowerCase()
      .replace(/[^\p{L}\p{N}%]+/gu, ' ')
      .trim();
  }

  /**
   * @param {string} query
   */
  #createEmptySearchMessage(query) {
    const item = document.createElement('li');
    item.className = 'collection-grid-search__empty-result';

    const heading = document.createElement('h2');
    heading.className = 'h3';
    heading.textContent = 'No matching products';

    const message = document.createElement('p');
    message.textContent = `No products in this collection match “${query}”.`;

    item.append(heading, message);
    return item;
  }

  #captureOriginalCountLabels() {
    this.#originalCountLabels = Array.from(this.querySelectorAll('[data-testid="products-count"] span')).map(
      (element) => element.textContent ?? ''
    );
  }

  /**
   * @param {number | string} value
   */
  #setCountLabel(value) {
    const labels = this.querySelectorAll('[data-testid="products-count"] span');

    labels.forEach((label) => {
      if (typeof value === 'number') {
        label.textContent = `${value} ${value === 1 ? 'item' : 'items'}`;
      } else {
        label.textContent = value;
      }
    });
  }

  #restoreCountLabels() {
    const labels = this.querySelectorAll('[data-testid="products-count"] span');

    labels.forEach((label, index) => {
      label.textContent = this.#originalCountLabels[index] ?? label.textContent;
    });
  }
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}